import { z } from 'zod';

/**
 * G14 — Plesk workload migration: shared contract.
 *
 * A migration workflow tracks one subscription's journey from "exists in
 * the G8 inventory" to "serving from Coolify, Plesk decommissionable".
 * The workflow state machine is deliberately small and forward-moving;
 * the only backward edge is the rollback path (cutover → verified via
 * DNS revert). Everything finer-grained lives on the per-site and
 * per-database rows, which the workflow transitions gate on.
 */

// ── Workflow state machine ──────────────────────────────────────────

export const MIGRATION_WORKFLOW_STATES = [
  'inventoried',
  'planned',
  'content_moved',
  'verified',
  'cutover',
  'done',
] as const;
export type MigrationWorkflowState = (typeof MIGRATION_WORKFLOW_STATES)[number];

/**
 * Legal transitions. Forward along the pipeline, plus the one rollback
 * edge: `cutover → verified` (DNS reverted, subscription serves from
 * Plesk again, verification can be redone before another attempt).
 */
export const MIGRATION_WORKFLOW_TRANSITIONS: Record<
  MigrationWorkflowState,
  readonly MigrationWorkflowState[]
> = {
  inventoried: ['planned'],
  planned: ['content_moved'],
  content_moved: ['verified'],
  verified: ['cutover'],
  cutover: ['done', 'verified'],
  done: [],
};

export function canTransitionWorkflow(
  from: MigrationWorkflowState,
  to: MigrationWorkflowState,
): boolean {
  return MIGRATION_WORKFLOW_TRANSITIONS[from].includes(to);
}

// ── Site build paths & states ───────────────────────────────────────

/**
 * The build path decides what the site becomes in Coolify:
 * - `static`     — file-serving site; nginx container + content volume
 *                  (or the real `static` build pack when a git repo exists).
 * - `php`        — PHP site; generated Dockerfile on the matching
 *                  php:<version>-fpm image + nginx, content volume.
 * - `nixpacks`   — an application with a build manifest; requires a git
 *                  repository (content transfer can't reproduce a build).
 * - `forwarding` — Plesk redirect-only domain; recreated as a static
 *                  redirect (runbook step, nothing to transfer).
 * - `none`       — no hosting (mail/DNS-only domain); nothing to build.
 */
export const MIGRATION_BUILD_PATHS = ['static', 'php', 'nixpacks', 'forwarding', 'none'] as const;
export type MigrationBuildPath = (typeof MIGRATION_BUILD_PATHS)[number];

export const MIGRATION_SITE_STATES = [
  'pending',
  'provisioned',
  'content_moved',
  'verified',
  'cutover',
  'done',
  'skipped',
] as const;
export type MigrationSiteState = (typeof MIGRATION_SITE_STATES)[number];

const SITE_STATE_RANK: Record<MigrationSiteState, number> = {
  pending: 0,
  provisioned: 1,
  content_moved: 2,
  verified: 3,
  cutover: 4,
  done: 5,
  skipped: 99,
};

export function siteStateRank(state: MigrationSiteState): number {
  return SITE_STATE_RANK[state];
}

export const MIGRATION_DATABASE_STATES = [
  'pending',
  'provisioned',
  'restored',
  'verified',
  'skipped',
] as const;
export type MigrationDatabaseState = (typeof MIGRATION_DATABASE_STATES)[number];

export const MIGRATION_STEP_STATUSES = ['queued', 'running', 'succeeded', 'failed'] as const;
export type MigrationStepStatus = (typeof MIGRATION_STEP_STATUSES)[number];

export const MIGRATION_STEP_KINDS = [
  'plan_probe',
  'provision_app',
  'transfer_content',
  'provision_database',
  'restore_database',
  'verify_database',
  'verify_site',
  'dns_cutover',
  'dns_revert',
] as const;
export type MigrationStepKind = (typeof MIGRATION_STEP_KINDS)[number];

// ── Transition guards ───────────────────────────────────────────────

export interface MigrationTransitionContext {
  hasPlan: boolean;
  /** Non-skipped sites only. */
  sites: { state: MigrationSiteState }[];
  /** Non-skipped databases only. */
  databases: { state: MigrationDatabaseState }[];
  /** Mail cutover gate: required when the subscription has mailboxes. */
  mail: {
    required: boolean;
    cutoverCompleted: boolean;
    /** Persisted, full-inventory mail evidence for workflows with no web/database work. */
    contentMoved?: boolean;
    verified?: boolean;
    cutoverStarted?: boolean;
    rollbackReady?: boolean;
  };
}

/**
 * Why a transition is not allowed right now — `null` means it may
 * proceed. Pure so the web app can explain the gate without a request
 * and tests can enumerate the matrix.
 */
export function migrationTransitionError(
  from: MigrationWorkflowState,
  to: MigrationWorkflowState,
  ctx: MigrationTransitionContext,
): string | null {
  if (!canTransitionWorkflow(from, to)) {
    return `Cannot move from ${from} to ${to}`;
  }
  const sitesAtLeast = (state: MigrationSiteState) =>
    ctx.sites.every((s) => siteStateRank(s.state) >= siteStateRank(state));
  const noWebWork = ctx.sites.length === 0 && ctx.databases.length === 0;
  if (noWebWork) {
    if (!ctx.hasPlan) return 'Generate a migration plan first';
    if (!ctx.mail.required) return 'Nothing to migrate — every site and database is skipped';
    switch (to) {
      case 'planned': return null;
      case 'content_moved':
        return ctx.mail.contentMoved === true ? null : 'Transfer every inventoried mailbox before continuing';
      case 'verified':
        if (from === 'cutover') {
          return ctx.mail.rollbackReady === true ? null : 'Withdraw the mailbox cutover before returning to verification';
        }
        return ctx.mail.verified === true ? null : 'Verify every mailbox, selected mail rules, existing passwords and webmail access';
      case 'cutover':
        return ctx.mail.cutoverStarted === true ? null : 'Complete the mailbox DNS cutover before continuing';
      case 'done':
        return ctx.mail.cutoverCompleted ? null : 'Mailbox observation, delivery acceptance and source drainage have not completed';
    }
  }
  switch (to) {
    case 'planned':
      if (!ctx.hasPlan) return 'Generate a migration plan first';
      return null;
    case 'content_moved':
      if (!sitesAtLeast('content_moved')) {
        return 'Every site needs its application provisioned and content transferred';
      }
      if (!ctx.databases.every((d) => d.state === 'restored' || d.state === 'verified')) {
        return 'Every database needs to be restored into Coolify';
      }
      return null;
    case 'verified':
      if (from === 'cutover') return null; // rollback edge: DNS reverted, gates already held
      if (!sitesAtLeast('verified')) {
        return 'Every site needs its acceptance checklist completed';
      }
      if (!ctx.databases.every((d) => d.state === 'verified')) {
        return 'Every database needs row-count verification';
      }
      return null;
    case 'cutover':
      if (!sitesAtLeast('cutover')) {
        return 'Every site needs its DNS cutover executed or confirmed';
      }
      return null;
    case 'done':
      if (ctx.mail.required && !ctx.mail.cutoverCompleted) {
        return 'Mailbox-migration cutover re-sync has not completed for this subscription';
      }
      return null;
    default:
      return null;
  }
}

// ── Plan shapes ─────────────────────────────────────────────────────

/** Read-only SSH evidence about a docroot; null when the probe didn't run. */
export interface MigrationDocrootProbeDto {
  phpFiles: number;
  hasIndexPhp: boolean;
  hasIndexHtml: boolean;
  hasComposerJson: boolean;
  hasPackageJson: boolean;
  hasWpConfig: boolean;
  hasArtisan?: boolean;
  hasDockerfile?: boolean;
  /** Relative file names only; file contents are never read. */
  environmentReferences?: string[];
  configReferences?: string[];
  startupReferences?: string[];
  lockfileReferences?: string[];
  /** `du -sk` of the docroot; null when unavailable. */
  sizeKb: number | null;
}

export type MigrationGitAccess = 'public' | 'private' | 'unknown';
export type MigrationGitCreationPath = 'public' | 'github-app' | 'deploy-key' | 'operator-review';

export interface MigrationGitSourceDto {
  repositoryName: string;
  repositoryUrl: string;
  host: string;
  path: string;
  branch: string;
  access: MigrationGitAccess;
  creationPath: MigrationGitCreationPath;
  deploymentMode: 'auto' | 'manual' | 'none' | 'unknown';
  deploymentPath: string | null;
  skipSslVerification: boolean | null;
  postDeployActions: {
    configured: boolean | null;
    disposition: 'none' | 'operator-review' | 'unknown';
    commandBodiesCollected: false;
    inventoryExecution: 'never';
    sourceMutation: 'never';
  };
}

export type MigrationRuntimeKind = 'nodejs' | 'laravel' | 'php' | 'static' | 'redirect';

export interface MigrationRuntimePlanDto {
  kind: MigrationRuntimeKind;
  version: string | null;
  pinned: boolean;
  buildPack: 'nixpacks' | 'dockerfile' | 'static';
  installCommand: string | null;
  buildCommand: string | null;
  startCommand: string | null;
  baseDirectory: string | null;
  publishDirectory: string | null;
  environmentReferences: string[];
  configReferences: string[];
  startupReferences: string[];
  evidence: string[];
  reviewReasons: string[];
}

export interface MigrationSiteZoneDto {
  /** Panel DNS zone (G12) covering this site's domain; null when none. */
  zoneName: string | null;
  panelManaged: boolean;
  /** The G8 inventory saw a Plesk-hosted zone for this domain. */
  pleskHosted: boolean;
}

export type MigrationDestinationShape =
  | 'application'
  | 'shared-application'
  | 'redirect-resource'
  | 'dns-mail-only';

export type MigrationCertificateStrategy =
  | 'not-required'
  | 'reissue-lets-encrypt'
  | 'manual-export'
  | 'switch-to-lets-encrypt';

export interface MigrationCertificateIntentDto {
  kind: 'none' | 'lets-encrypt' | 'custom' | 'wildcard' | 'unknown';
  certificateName: string | null;
  strategy: MigrationCertificateStrategy | null;
  unattendedCutoverBlocked: boolean;
}

export interface MigrationDnsRollbackValueDto {
  fqdn: string;
  type: 'A' | 'AAAA' | 'CNAME';
  values: { content: string; ttl: number | null }[];
}

export interface MigrationTopologyNameDto {
  pleskDomainId: string;
  name: string;
  kind: 'site' | 'subdomain' | 'alias';
  parentDomain: string | null;
  destination: MigrationDestinationShape;
  /** Canonical application/redirect row that owns this name, if any. */
  applicationDomain: string | null;
  webEnabled: boolean;
  mailEnabled: boolean | null;
  docroot: string | null;
  forwarding: {
    mode: 'standard' | 'frame';
    destinationUrl: string | null;
    httpCode: 301 | 302 | null;
  } | null;
  certificate: MigrationCertificateIntentDto;
  dnsRollback: MigrationDnsRollbackValueDto[];
}

export const publishedMigrationContentSchema = z.object({
  directory: z.string().trim().min(1).max(255).regex(/^(\.|[A-Za-z0-9_-][A-Za-z0-9_.-]*(\/[A-Za-z0-9_-][A-Za-z0-9_.-]*)*)$/)
    .refine((value) => !value.includes('..'), 'Choose the current document root (.) or a published subdirectory'),
  spaFallback: z.boolean().default(false),
  followUpOwner: z.string().trim().min(1).max(200),
  notes: z.string().trim().min(1).max(1000),
  confirmStatic: z.literal(true),
}).strict();
export type PublishedMigrationContent = z.infer<typeof publishedMigrationContentSchema>;

export interface MigrationSitePlanDto {
  pleskDomainId: string;
  domain: string;
  domainType: 'site' | 'subdomain';
  buildPath: MigrationBuildPath;
  /** Explicit published-file migration; original Git/runtime evidence remains intact. */
  publishedContent?: PublishedMigrationContent | null;
  /** Human-readable classifier decision trail. */
  reason: string;
  docroot: string | null;
  phpVersion: string | null;
  sslEnabled: boolean | null;
  /** Certificate is Let's Encrypt (Coolify re-issues automatically). */
  letsEncrypt: boolean | null;
  probe: MigrationDocrootProbeDto | null;
  /** Generated Dockerfile for content paths (static/php); operator-editable. */
  dockerfile: string | null;
  /** Operator-supplied repository for the nixpacks/git path. */
  gitRepository: string | null;
  gitBranch: string | null;
  /** Fixed inventory evidence selected for this destination application. */
  gitSource: MigrationGitSourceDto | null;
  /** Runtime/build definition derived from the evidence hierarchy. */
  runtime: MigrationRuntimePlanDto;
  /** Portable destination health check; operator-editable before provision. */
  healthCheck: {
    enabled: boolean;
    path: string;
    port: string;
    method: 'GET';
    expectedStatus: number;
  };
  /** Source cron entries proven to target this application; staged disabled. */
  scheduledTasks: MigrationCronEntryDto[];
  contentBytes: number | null;
  /** Plesk alias domains pointing at this site (extra Coolify domains at cutover). */
  aliases: string[];
  /** Every host routed to this one destination resource, with its source role. */
  names: {
    name: string;
    kind: 'site' | 'subdomain' | 'alias';
    canonicalRedirect: boolean;
  }[];
  destinationShape: 'application' | 'redirect-resource';
  redirect: {
    mode: 'standard' | 'frame';
    destinationUrl: string | null;
    httpCode: 301 | 302 | null;
  } | null;
  certificate: MigrationCertificateIntentDto;
  hostingSettings: import('./plesk.js').PleskPortableHostingSettings | null;
  protectedPaths: import('./plesk.js').PleskProtectedPathPayload[];
  /** Frozen source values for every web name this row may cut over. */
  dnsRollback: MigrationDnsRollbackValueDto[];
  zone: MigrationSiteZoneDto;
  warnings: string[];
}

export interface MigrationDatabasePlanDto {
  pleskDatabaseId: string;
  name: string;
  /** Plesk engine: mysql, postgresql, mssql. */
  engine: string;
  /** Coolify engine to provision; null when unsupported (manual path). */
  targetEngine: string | null;
  supported: boolean;
  sizeBytes: number | null;
  reason: string;
}

export interface MigrationCronEntryDto {
  name: string;
  schedule: string;
  command: string;
  /** Site whose source docroot appears in the command, when unambiguous. */
  targetDomain: string | null;
  enabled: boolean;
  requiresReview: boolean;
  reviewReasons: string[];
}

export interface MigrationPlanDto {
  schemaVersion: 3;
  generatedAt: string;
  snapshotId: string;
  /** Plesk webspace (main domain) this plan was derived from. */
  subscriptionName: string;
  probeRan: boolean;
  /** One deterministic row per source name, including aliases/no-hosting. */
  topology: MigrationTopologyNameDto[];
  sites: MigrationSitePlanDto[];
  databases: MigrationDatabasePlanDto[];
  mail: { mailboxes: number; forwarders: number };
  /** Cron jobs to recreate on the Coolify side (runbook step). */
  cron: MigrationCronEntryDto[];
  warnings: string[];
}

// ── Checklist ───────────────────────────────────────────────────────

export const MIGRATION_CHECK_STATES = ['pending', 'passed', 'failed', 'skipped'] as const;
export type MigrationCheckState = (typeof MIGRATION_CHECK_STATES)[number];

export interface MigrationChecklistItemDto {
  key: string;
  label: string;
  /** `auto` items are set by the panel's staged verification; `manual` by staff. */
  kind: 'auto' | 'manual';
  state: MigrationCheckState;
  detail: string | null;
  checkedAt: string | null;
  checkedBy: string | null;
}

// ── Cutover record ──────────────────────────────────────────────────

export interface MigrationCutoverRecordDto {
  /** Exact panel zone containing this owner (supports multi-zone aliases). */
  zoneName?: string;
  /** Absolute host retained for operator-readable, per-name rollback evidence. */
  fqdn?: string;
  /** Zone-relative owner name (`@` = apex). */
  name: string;
  type: string;
  /**
   * Every content value this owner/type held before cutover — an array so
   * a round-robin record set (multiple A rows) is restored in full on
   * revert. Empty when the record didn't exist (revert then deletes it).
   */
  previousContents: string[];
  /** Exact destination rows, including TTL/origin, for lossless revert. */
  previousRecords?: {
    content: string;
    ttl: number;
    origin: 'user' | 'template' | 'auto';
    templateKey?: string | null;
    autoKey?: string | null;
  }[];
  /** null means cutover removed a conflicting AAAA/CNAME RRset. */
  newContent: string | null;
}

export interface MigrationCutoverDto {
  /** `panel` — records flipped in the G12 zone; `manual` — operator confirmed external DNS. */
  mode: 'panel' | 'manual';
  at: string;
  by: string | null;
  targetIp: string | null;
  records: MigrationCutoverRecordDto[];
  /** Instructions shown for manual mode (external DNS host). */
  manualInstructions:
    | {
        name: string;
        type: string;
        content: string;
        previousValues?: { type: string; content: string; ttl: number | null }[];
      }[]
    | null;
  revertedAt: string | null;
}

// ── Row DTOs ────────────────────────────────────────────────────────

export interface MigrationWorkflowDto {
  id: string;
  subscriptionId: string;
  subscriptionName: string;
  clientName: string | null;
  snapshotId: string | null;
  state: MigrationWorkflowState;
  plan: MigrationPlanDto | null;
  stateHistory: { state: MigrationWorkflowState; at: string; by: string | null }[];
  createdAt: string;
  updatedAt: string;
}

export interface MigrationSiteDto {
  id: string;
  workflowId: string;
  domain: string;
  buildPath: MigrationBuildPath;
  state: MigrationSiteState;
  plan: MigrationSitePlanDto;
  appResourceId: string | null;
  appCoolifyUuid: string | null;
  stagingFqdn: string | null;
  contentSyncedAt: string | null;
  checklist: MigrationChecklistItemDto[];
  cutover: MigrationCutoverDto | null;
  lastError: string | null;
  updatedAt: string;
}

export interface MigrationDatabaseTableCountDto {
  table: string;
  source: number | null;
  target: number | null;
  match: boolean;
}

export interface MigrationDatabaseDto {
  id: string;
  workflowId: string;
  name: string;
  engine: string;
  targetEngine: string | null;
  state: MigrationDatabaseState;
  dbResourceId: string | null;
  dbCoolifyUuid: string | null;
  sizeBytes: number | null;
  targetDatabaseName: string | null;
  tableCounts: MigrationDatabaseTableCountDto[] | null;
  verifiedAt: string | null;
  lastError: string | null;
  updatedAt: string;
}

export interface MigrationStepDto {
  id: string;
  workflowId: string;
  siteId: string | null;
  databaseId: string | null;
  kind: MigrationStepKind;
  status: MigrationStepStatus;
  /** Bounded tail of command output — diagnostics, not a full log. */
  output: string | null;
  error: string | null;
  createdAt: string;
  startedAt: string | null;
  finishedAt: string | null;
  triggeredBy: string | null;
}

export interface MigrationMailSummaryDto {
  mailboxes: number;
  /** Latest initial-phase batch, when any. */
  initial: { id: string; status: string } | null;
  /** Latest cutover-phase batch, when any. */
  cutover: { id: string; status: string } | null;
}

export interface MigrationWorkflowDetailDto {
  workflow: MigrationWorkflowDto;
  sites: MigrationSiteDto[];
  databases: MigrationDatabaseDto[];
  steps: MigrationStepDto[];
  mail: MigrationMailSummaryDto;
  /** Transitions whose guard currently passes. */
  eligibleTransitions: MigrationWorkflowState[];
  /** Guard explanations for the ones that don't. */
  blockedTransitions: { to: MigrationWorkflowState; reason: string }[];
}

export interface MigrationOverviewDto {
  workflows: MigrationWorkflowDto[];
  /** Subscriptions with a Plesk external ref but no workflow yet. */
  eligible: {
    subscriptionId: string;
    subscriptionName: string;
    clientName: string | null;
    pleskName: string | null;
  }[];
  configured: boolean;
}

// ── Settings ────────────────────────────────────────────────────────

export interface MigrationSettingsDto {
  sshHost: string;
  sshPort: number;
  sshUser: string;
  hasPrivateKey: boolean;
}

export const migrationSettingsSchema = z.object({
  sshHost: z.string().min(1, 'Source host is required'),
  sshPort: z.coerce.number().int().min(1).max(65535).default(22),
  sshUser: z.string().min(1).default('root'),
  /** Write-only; omit to keep the stored key. */
  privateKey: z.string().trim().min(1).optional(),
});
export type MigrationSettingsInput = z.infer<typeof migrationSettingsSchema>;

// ── Action inputs ───────────────────────────────────────────────────

export const startMigrationWorkflowSchema = z.object({
  subscriptionId: z.string().uuid(),
  /** Defaults to the newest completed/partial snapshot. */
  snapshotId: z.string().uuid().optional(),
});
export type StartMigrationWorkflowInput = z.infer<typeof startMigrationWorkflowSchema>;

export const generateMigrationPlanSchema = z.object({
  /** Probe docroots over SSH for classification evidence (read-only). */
  probe: z.boolean().default(true),
});
export type GenerateMigrationPlanInput = z.infer<typeof generateMigrationPlanSchema>;

export const updateMigrationSitePlanSchema = z.object({
  publishedContent: publishedMigrationContentSchema.nullable().optional(),
  buildPath: z.enum(MIGRATION_BUILD_PATHS).optional(),
  dockerfile: z.string().max(20_000).nullable().optional(),
  gitRepository: z.string().max(500).nullable().optional(),
  gitBranch: z.string().max(200).nullable().optional(),
  gitCreationPath: z
    .enum(['public', 'github-app', 'deploy-key', 'operator-review'])
    .optional(),
  installCommand: z.string().max(1000).nullable().optional(),
  buildCommand: z.string().max(1000).nullable().optional(),
  startCommand: z.string().max(1000).nullable().optional(),
  baseDirectory: z.string().max(255).nullable().optional(),
  publishDirectory: z.string().max(255).nullable().optional(),
  healthCheck: z
    .object({
      enabled: z.boolean(),
      path: z.string().trim().min(1).max(255),
      port: z.string().trim().regex(/^\d+$/),
      method: z.literal('GET'),
      expectedStatus: z.number().int().min(100).max(599),
    })
    .optional(),
  certificateStrategy: z
    .enum([
      'not-required',
      'reissue-lets-encrypt',
      'manual-export',
      'switch-to-lets-encrypt',
    ])
    .nullable()
    .optional(),
  skipped: z.boolean().optional(),
});
export type UpdateMigrationSitePlanInput = z.infer<typeof updateMigrationSitePlanSchema>;

export const provisionMigrationSiteSchema = z.object({
  serverUuid: z.string().min(1),
  /** Required when the plan selects the private GitHub App creation path. */
  githubAppUuid: z.string().trim().min(1).max(100).optional(),
  /** Required when the plan selects the private deploy-key creation path. */
  privateKeyUuid: z.string().trim().min(1).max(100).optional(),
});
export type ProvisionMigrationSiteInput = z.infer<typeof provisionMigrationSiteSchema>;

export const provisionMigrationDatabaseSchema = z.object({
  serverUuid: z.string().min(1),
});
export type ProvisionMigrationDatabaseInput = z.infer<typeof provisionMigrationDatabaseSchema>;

export const updateMigrationChecklistSchema = z.object({
  key: z.string().min(1),
  state: z.enum(MIGRATION_CHECK_STATES),
  detail: z.string().max(2000).nullable().optional(),
});
export type UpdateMigrationChecklistInput = z.infer<typeof updateMigrationChecklistSchema>;

export const cutoverMigrationSiteSchema = z.object({
  /** Cutover changes live DNS — an explicit confirmation is required. */
  confirm: z.literal(true),
  /** Mark a manually-executed cutover (external DNS host) as done. */
  manual: z.boolean().default(false),
  /** Explicit public A-record destination when Coolify uses an internal host. */
  targetIpv4: z.string().trim().ip({ version: 'v4' }).optional(),
});
export type CutoverMigrationSiteInput = z.infer<typeof cutoverMigrationSiteSchema>;

export const transitionMigrationWorkflowSchema = z.object({
  to: z.enum(MIGRATION_WORKFLOW_STATES),
});
export type TransitionMigrationWorkflowInput = z.infer<typeof transitionMigrationWorkflowSchema>;
